import { useEffect, useState } from "react";
import { Eye, EyeOff, Loader2, MessageSquareReply, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import Stars from "@/components/Stars";

const STATUS = { published: ["Publié", "bg-sage-light text-sage"], pending: ["À valider", "bg-amber-100 text-amber-900"], hidden: ["Masqué", "bg-stone-100 text-stone-mute"] };
const input = "mt-1 w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-2.5 text-sm outline-none focus:border-amber-deep";

function Toggle({ k, label, form, setForm }) {
  return (
    <label className="flex items-center gap-2 text-sm font-medium text-bark">
      <input data-testid={`reviews-setting-${k}`} type="checkbox" checked={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="h-4 w-4 accent-amber-deep" /> {label}
    </label>
  );
}

function SettingsCard() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/reviews/settings").then(({ data }) => setForm((c) => c || data)).catch(() => {}); }, []);
  if (!form) return null;
  const field = (k, label, extra = {}) => (
    <label className="text-sm font-medium text-bark">{label}
      <input data-testid={`reviews-setting-${k}`} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: extra.type === "number" ? Number(e.target.value) : e.target.value })} className={input} {...extra} />
    </label>
  );
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/reviews/settings", form);
      setForm(data);
      toast.success("Réglages des avis enregistrés");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="reviews-settings" onSubmit={save} className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
      <h2 className="font-serif text-xl font-semibold text-bark">Réglages</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Toggle k="invite_enabled" label="Envoyer l'invitation à donner un avis" form={form} setForm={setForm} />
        <span />
        {field("invite_delay_days", "Délai après la commande (jours)", { type: "number", min: 1, max: 90 })}
        {field("auto_publish_min", "Publication automatique à partir de (étoiles)", { type: "number", min: 1, max: 6 })}
        {field("site_points", "Points fidélité pour l'avis sur le site", { type: "number", min: 0, max: 1000 })}
        {field("product_points", "Points fidélité par produit noté", { type: "number", min: 0, max: 1000 })}
      </div>
      <div className="grid gap-4 border-t border-amber-900/10 pt-5 sm:grid-cols-2">
        <Toggle k="google_enabled" label="Avis Google" form={form} setForm={setForm} />
        <p data-testid="reviews-google-key-status" className={`text-xs ${form.google_key_set ? "text-sage" : "text-terra"}`}>{form.google_key_set ? "Clé Google enregistrée" : "Clé Google manquante : les avis Google restent masqués"}</p>
        {field("google_place_id", "Place ID Google")}
        {field("google_review_url", "Lien « Laisser un avis » (facultatif)", { placeholder: "https://…" })}
      </div>
      <div className="grid gap-4 border-t border-amber-900/10 pt-5 sm:grid-cols-2">
        <Toggle k="facebook_enabled" label="Avis Facebook (masqués tant que décoché)" form={form} setForm={setForm} />
        <span />
        {field("facebook_page_url", "Lien de votre page Facebook (avis)", { placeholder: "https://www.facebook.com/…/reviews" })}
        {field("facebook_page_id", "Identifiant de la page Facebook")}
      </div>
      <button data-testid="reviews-settings-save" disabled={busy} className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-2.5 text-sm font-semibold text-white hover:bg-honey disabled:opacity-60">
        {busy && <Loader2 size={15} className="animate-spin" />} Enregistrer
      </button>
    </form>
  );
}

function ReplyEditor({ review, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(review.reply || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/reviews/${review.id}/reply`, { reply: text });
      toast.success(text.trim() ? "Réponse publiée" : "Réponse supprimée");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <div className="mt-2">
        {review.reply && <p data-testid={`admin-review-reply-text-${review.id}`} className="border-l-2 border-amber-deep/40 pl-3 text-xs text-bark-soft"><strong className="text-amber-deep">Votre réponse :</strong> {review.reply}</p>}
        <button data-testid={`admin-review-reply-${review.id}`} onClick={() => setOpen(true)} className="mt-2 flex items-center gap-1 text-xs font-medium text-amber-deep hover:underline">
          <MessageSquareReply size={12} /> {review.reply ? "Modifier la réponse" : "Répondre"}
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2">
      <textarea data-testid={`admin-review-reply-input-${review.id}`} rows={3} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre réponse publique (laissez vide pour la supprimer)" className="w-full rounded-xl border border-amber-900/20 bg-cream px-3 py-2 text-sm outline-none focus:border-amber-deep" />
      <div className="flex gap-2">
        <button data-testid={`admin-review-reply-save-${review.id}`} disabled={busy} onClick={save} className="flex items-center gap-1 rounded-full bg-amber-deep px-4 py-1.5 text-xs font-semibold text-white hover:bg-honey disabled:opacity-60">{busy && <Loader2 size={12} className="animate-spin" />} Publier la réponse</button>
        <button onClick={() => { setOpen(false); setText(review.reply || ""); }} className="rounded-full border border-amber-900/20 px-4 py-1.5 text-xs text-bark">Annuler</button>
      </div>
    </div>
  );
}

export default function AdminReviews() {
  const [reviews, setReviews] = useState(null);
  const load = () => api.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => setReviews([]));
  useEffect(() => { load(); }, []);

  const setStatus = (id, status) => api.patch(`/admin/reviews/${id}`, { status }).then(load).catch(() => toast.error("Action impossible"));
  const remove = (id) => window.confirm("Supprimer définitivement cet avis ?") && api.delete(`/admin/reviews/${id}`).then(load);

  return (
    <div data-testid="admin-reviews" className="space-y-8">
      <div>
        <h1 className="font-serif text-4xl font-semibold text-bark">Avis clients</h1>
        <p className="mt-1 text-sm text-bark-soft">Les avis publiés apparaissent dans le carrousel en bas de page, avec vos avis Google (et Facebook une fois activé).</p>
      </div>
      <SettingsCard />
      {reviews === null ? <Loader2 className="animate-spin text-amber-deep" /> : reviews.length === 0 ? (
        <p data-testid="reviews-empty" className="text-sm text-bark-soft">Aucun avis reçu pour le moment.</p>
      ) : (
        <ul className="space-y-3">
          {reviews.map((r) => (
            <li key={r.id} data-testid={`admin-review-${r.id}`} className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-amber-800/15 bg-white p-5">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-bark">
                  {r.author} <Stars value={r.rating} size={13} />
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>
                </p>
                <p className="text-xs text-stone-mute">{r.type === "site" ? "Avis sur le site" : `Produit : ${r.product_name}`} · {new Date(r.created_at).toLocaleDateString("fr-FR")} · {r.email}</p>
                {r.comment && <p className="mt-2 text-sm text-bark-soft">« {r.comment} »</p>}
                <ReplyEditor review={r} onSaved={load} />
              </div>
              <div className="flex gap-2">
                {r.status !== "published" ? (
                  <button data-testid={`admin-review-publish-${r.id}`} onClick={() => setStatus(r.id, "published")} className="flex items-center gap-1 rounded-full bg-amber-deep px-3 py-1.5 text-xs font-medium text-white hover:bg-honey"><Eye size={12} /> Publier</button>
                ) : (
                  <button data-testid={`admin-review-hide-${r.id}`} onClick={() => setStatus(r.id, "hidden")} className="flex items-center gap-1 rounded-full border border-amber-800/25 px-3 py-1.5 text-xs font-medium text-bark hover:border-amber-deep"><EyeOff size={12} /> Masquer</button>
                )}
                <button data-testid={`admin-review-delete-${r.id}`} onClick={() => remove(r.id)} className="rounded-full p-1.5 text-terra hover:bg-terra/10"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
